/**
 * URL query-param helpers for the auth "front door" pages.
 *
 * Centralises the read/strip pattern that AuthLayout (`?error=`), LoginPage
 * (`?verified=1`) and VerifyEmailPage (`?email=`) each hand-rolled. All are
 * SSR-safe (return null when `window` is absent) and decode via URLSearchParams.
 */

/** Read a query param without mutating the URL. Returns null if absent/SSR. */
export function readQueryParam(name: string): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(name);
}

/**
 * Read a query param and strip it (plus any `also` companions) from the URL via
 * history.replaceState, so the value doesn't survive a refresh/bookmark.
 * Returns the value, or null if absent/SSR.
 */
export function consumeQueryParam(name: string, also: readonly string[] = []): string | null {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  const value = url.searchParams.get(name);
  if (value === null) return null;
  url.searchParams.delete(name);
  for (const k of also) url.searchParams.delete(k);
  window.history.replaceState({}, '', url.pathname + url.search);
  return value;
}

/**
 * Validate a post-login redirect target. Only same-origin absolute PATHS are
 * allowed — a value must start with a single `/`. Protocol-relative (`//host`),
 * backslash (`/\host`, which browsers treat as `//`) and absolute URLs
 * (`https://…`) are rejected, since navigating to them would be an open
 * redirect. Returns the safe path, or undefined.
 */
export function safeRedirectPath(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length === 0) return undefined;
  if (!value.startsWith('/')) return undefined;
  if (value.startsWith('//') || value.startsWith('/\\')) return undefined;
  return value;
}
